import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { PieChart, ArrowRightLeft, WalletIcon, HistoryIcon, CheckCircle2, UserIcon, UsersIcon, GamepadIcon } from 'lucide-react';
const outcomes = [{
  icon: PieChart,
  title: 'Equal and custom splits',
  description: 'Split costs evenly or enter exact custom amounts for each person.'
}, {
  icon: ArrowRightLeft,
  title: 'Simplest way to settle',
  description: 'See who pays who and how much, without working it out yourself.'
}, {
  icon: WalletIcon,
  title: 'My Balance',
  description: "See what you're owed and what you owe across all your kitties."
}, {
  icon: HistoryIcon,
  title: 'Expense history',
  description: 'Keep a clear record of what was added, who paid, and how it was split.'
}, {
  icon: CheckCircle2,
  title: 'Payment tracking',
  description: 'Track payments as unpaid, pending or confirmed after people pay outside KittyPot.'
}, {
  icon: UserIcon,
  title: 'Guest mode',
  description: 'Try KittyPot locally on your device before creating an account.'
}, {
  icon: UsersIcon,
  title: 'Share with',
  description: 'Choose exactly who an expense is for, then split equally or by custom amount.'
}, {
  icon: GamepadIcon,
  title: 'Group games',
  description: "Add a bit of fun with lightweight group games like 'Who's round is next?'"
}];
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
export function ProductDeepDive() {
  return <section id="features" className="py-24 lg:py-32 bg-gray-950 relative overflow-hidden" data-id="element-209">
      {/* Subtle radial glow */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-210">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-kitty-yellow/5 rounded-full blur-3xl" data-id="element-211" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-212">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-2xl mx-auto mb-16 lg:mb-20" data-id="element-213">
          <p className="text-sm font-semibold text-kitty-yellow uppercase tracking-widest mb-4" data-id="element-214">
            Features
          </p>
          <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-5 tracking-tight leading-tight" data-id="element-215">
            Everything you need to track shared expenses.
          </h2>
          <p className="text-lg text-gray-400 max-w-lg mx-auto leading-relaxed" data-id="element-216">
            No spreadsheets. No stress. No chasing.
          </p>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-60px'
      }} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-217">
          {outcomes.map(item => <motion.div key={item.title} variants={itemVariants} className="rounded-2xl border border-gray-800 bg-gray-900/60 backdrop-blur-sm p-6 lg:p-8 group" data-id="element-218">
              <div className="w-12 h-12 rounded-xl bg-kitty-yellow/10 flex items-center justify-center mb-5 group-hover:bg-kitty-yellow/20 transition-colors duration-300" data-id="element-219">
                <item.icon className="w-6 h-6 text-kitty-yellow" data-id="element-220" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2 leading-snug" data-id="element-221">
                {item.title}
              </h3>
              <p className="text-sm text-gray-400 leading-relaxed" data-id="element-222">
                {item.description}
              </p>
            </motion.div>)}
        </motion.div>

        {/* Confidence line */}
        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.5
      }} className="text-center text-gray-500 text-sm mt-12 lg:mt-16" data-id="element-223">
          Built for groups who'd rather enjoy the moment than do the maths.
        </motion.p>
      </div>
    </section>;
}